import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { PublicScanPrompt } from '@brand24-original/llm-types';
import ReportSection from './ReportSection';
import './ReportPromptAnalysis.scss';

interface ReportPromptAnalysisProps {
  prompts?: PublicScanPrompt[];
  /**
   * Darmowy Checker: zamiast danych rysujemy zamazany teaser z zachętą do założenia konta. Backend
   * i tak nie wysyła treści promptów (`PublicScanReportResponse.prompts` jest opcjonalne), więc blur
   * jest ozdobą — zabezpieczeniem jest brak danych w payloadzie.
   */
  locked?: boolean;
  /** Dokąd prowadzi przycisk w teaserze. */
  signUpHref?: string;
}

/**
 * Skrajne prompty: albo obie strony rankingu, albo informacja, że wynik jest wszędzie równy.
 * Rozróżnia je pole `kind` — bez dyskryminatora (samo `'even' in extremes`) TS nie zawęża wariantu
 * i wartość wychodzi jako `number | undefined`.
 */
type PromptExtremes =
  | { kind: 'even'; value: number }
  | { kind: 'range'; best: PublicScanPrompt; worst: PublicScanPrompt };

/** Ile pozycji ma ranking. Checker ma mniej promptów, więc mieści się cały. */
const TOP_PROMPTS = 10;

/** Prompt idzie w cudzysłowie jako cytat pytania; nazwa grupy to etykieta, więc zostaje bez niego. */
const promptLabel = (entry: PublicScanPrompt): string =>
  entry.groupSize === undefined ? `"${entry.prompt?.trim()}"` : entry.prompt.trim();

/**
 * Atrapa dla darmowego Checkera. Renderuje się DOKŁADNIE tą samą ścieżką, co prawdziwa sekcja —
 * dzięki temu układ pod rozmyciem jest identyczny (kafle skrajne + panel z rankingiem), zamiast
 * własnego markupu, który rozjeżdżał się z oryginałem. Treści są jawnie fikcyjne, a i tak nikt ich
 * nie odczyta: sekcja jest rozmyta, a backend nie wysyła prawdziwych promptów.
 */
const LOCKED_TEASER_PROMPTS: PublicScanPrompt[] = [
  { prompt: 'Lorem ipsum dolor sit amet consectetur', visibility: 92, brands: [] },
  { prompt: 'Sed do eiusmod tempor incididunt ut labore', visibility: 78, brands: [] },
  { prompt: 'Ut enim ad minim veniam quis nostrud', visibility: 71, brands: [] },
  { prompt: 'Duis aute irure dolor in reprehenderit', visibility: 64, brands: [] },
  { prompt: 'Excepteur sint occaecat cupidatat non proident', visibility: 55, brands: [] },
  { prompt: 'Sunt in culpa qui officia deserunt mollit', visibility: 47, brands: [] },
  { prompt: 'Nemo enim ipsam voluptatem quia voluptas', visibility: 39, brands: [] },
  { prompt: 'Neque porro quisquam est qui dolorem ipsum', visibility: 31, brands: [] },
  { prompt: 'Quis autem vel eum iure reprehenderit', visibility: 24, brands: [] },
  { prompt: 'At vero eos et accusamus et iusto odio', visibility: 12, brands: [] },
];

export const ReportPromptAnalysis: React.FC<ReportPromptAnalysisProps> = ({
  prompts = [],
  locked = false,
  signUpHref,
}) => {
  const { t } = useTranslation();

  const source = locked ? LOCKED_TEASER_PROMPTS : prompts;
  const sorted = useMemo(
    () => [...source].sort((a, b) => b.visibility - a.visibility),
    [source]
  );

  /**
   * Najsłabszy prompt wychodzi POD listę, w osobny wiersz za separatorem — wcześniej doklejał się
   * jako ostatnia pozycja `<ol>` i czytał się jak „dziesiąty najlepszy", mimo że niesie swój
   * prawdziwy numer w rankingu (przy 20 promptach będzie to 20).
   *
   * `hasGap` = między listą a najsłabszym są pominięte prompty. Tylko wtedy nagłówek dostaje „…";
   * gdy numer jest po prostu kolejny (lista 1–9, najsłabszy 10), wielokropek sugerowałby przeskok,
   * którego nie ma, więc zostaje sam separator.
   */
  const { listed, lowest, hasGap } = useMemo(() => {
    const ranked = sorted.map((prompt, index) => ({ prompt, rank: index + 1 }));
    // Jeden prompt nie ma „najsłabszego" — ten sam wiersz nie może stać w obu miejscach.
    if (ranked.length < 2) {
      return { listed: ranked, lowest: null, hasGap: false };
    }

    const worst = ranked[ranked.length - 1];
    const top = ranked.slice(0, Math.min(TOP_PROMPTS - 1, ranked.length - 1));
    return { listed: top, lowest: worst, hasGap: worst.rank > top[top.length - 1].rank + 1 };
  }, [sorted]);

  /**
   * Skrajne prompty — liczone z CAŁEJ listy, nie z widocznej dziesiątki. Nie ma ich, gdy wszystkie
   * mają zero (nic nie wyróżnia) albo gdy wszystkie mają tyle samo — wtedy „najwyższa" i „najniższa"
   * wskazywałyby tę samą wartość i sugerowały różnicę, której nie ma. W tym drugim przypadku mówimy
   * wprost, że wynik jest równy.
   */
  const extremes = useMemo<PromptExtremes | null>(() => {
    if (sorted.length < 2 || sorted[0].visibility === 0) {
      return null;
    }

    const best = sorted[0];
    const worst = sorted[sorted.length - 1];
    return best.visibility === worst.visibility
      ? { kind: 'even', value: best.visibility }
      : { kind: 'range', best, worst };
  }, [sorted]);

  // Slajd bez danych w ogóle nie wchodzi do raportu — ani na stronie, ani w PDF.
  if (source.length === 0) {
    return null;
  }

  const hasGroups = source.some((prompt) => prompt.groupSize !== undefined);

  /**
   * Tag odróżniający wiersz grupy od wiersza promptu — jedyne, co grupę wyróżnia w układzie, bo poza
   * nim liczy się i rysuje dokładnie jak pojedyncze pytanie. W liście starcza sama liczba promptów
   * (obok stoi ich temat), w kaflu skrajnym idzie pełne „Grupa · N promptów", bo etykieta kafla mówi
   * o widoczności, a nie o tym, czego dotyczy. Ikona jest tylko na liście: w kaflu tag stoi w jednej
   * linii z etykietą i musi być możliwie wąski, żeby jej nie zepchnąć do drugiego wiersza.
   */
  const groupTag = (size: number, withCount: boolean) =>
    withCount ? (
      <span className="report-prompts__group-tag">
        {t('reportPromptsGroupTagCount', { count: size })}
      </span>
    ) : (
      <span className="report-prompts__group-tag">
        <span className="ico--folder ico--xxs" aria-hidden="true" />
        {`${t('reportPromptsGroupTag')} ${size}`}
      </span>
    );

  const list = (
    <>
      <h3 className="report-section__panel-title">
        {t(hasGroups ? 'reportPromptsTopTitleGroups' : 'reportPromptsTopTitle')}
      </h3>
      <p className="report-section__panel-desc">
        {t(hasGroups ? 'reportPromptsTopDescGroups' : 'reportPromptsTopDesc')}
      </p>

      <ol className="report-prompts__list">
        {listed.map(({ prompt, rank }) => (
          <li key={prompt.prompt} className="report-prompts__row">
            <span className="report-prompts__rank">{rank}</span>
            <div className="report-prompts__content">
              <span className="report-prompts__text" title={prompt.prompt}>
                {prompt.groupSize !== undefined && groupTag(prompt.groupSize, false)}
                {prompt.prompt}
              </span>
              <div className="report-section__track">
                <div
                  className="report-section__bar"
                  style={{ width: `${Math.min(prompt.visibility, 100)}%` }}
                />
              </div>
            </div>
            <span className="report-prompts__value">{prompt.visibility}%</span>
          </li>
        ))}
      </ol>

      {lowest && (
        <div className="report-prompts__lowest">
          <span className="report-prompts__lowest-label">
            {hasGap && (
              <span className="report-prompts__lowest-gap" aria-hidden="true">
                <span className="ico--more ico--xs" />
              </span>
            )}
            {t('reportPromptsLowest')}
          </span>
          <div className="report-prompts__row report-prompts__row--lowest">
            <span className="report-prompts__rank">{lowest.rank}</span>
            <div className="report-prompts__content">
              <span className="report-prompts__text" title={lowest.prompt.prompt}>
                {lowest.prompt.groupSize !== undefined && groupTag(lowest.prompt.groupSize, false)}
                {lowest.prompt.prompt}
              </span>
              <div className="report-section__track">
                <div
                  className="report-section__bar"
                  style={{ width: `${Math.min(lowest.prompt.visibility, 100)}%` }}
                />
              </div>
            </div>
            <span className="report-prompts__value">{lowest.prompt.visibility}%</span>
          </div>
        </div>
      )}
    </>
  );

  /**
   * Kafel skrajnego promptu: ikona trendu po lewej, treść po prawej. Ikony są te same, co w trendach
   * w panelu (`ico--chart-up` / `ico--chart-down`), więc kierunek czyta się bez czytania etykiety.
   */
  const extremeCard = (
    modifier: 'best' | 'worst',
    icon: string,
    label: string,
    body: React.ReactNode,
    value: number,
    groupSize?: number
  ) => (
    <div className={`report-prompts__extreme report-prompts__extreme--${modifier}`}>
      <span className="report-prompts__extreme-icon" aria-hidden="true">
        <span className={`${icon} ico--sm`} />
      </span>
      <div className="report-prompts__extreme-content">
        <span className="report-prompts__extreme-label">{label}</span>
        {/* Tag stoi przy NAZWIE grupy, nie przy etykiecie: tam jest miejsce na zawinięcie, a przy
            etykiecie kafla wypychał ją do drugiej linii albo sam z niej spadał. */}
        <p className="report-prompts__extreme-body">
          {groupSize !== undefined && groupTag(groupSize, true)}
          <span className="report-prompts__extreme-prompt">{body}</span>
        </p>
      </div>
      {/* Osobna kolumna kafla, nie część treści — inaczej liczba wyrównuje się do promptu i siedzi
          wyraźnie poniżej osi, na której stoi ikona. */}
      <span className="report-prompts__extreme-value">{value}%</span>
    </div>
  );

  // Bez kafli skrajnych lista zajmuje całą sekcję — zostaje sam nagłówek, bez ramki wokół.
  const extremesBlock = extremes && (
    <div className="report-prompts__extremes">
      {extremes.kind === 'even' ? (
        // Równy wynik to nadal kafel „na plus" — zostaje standardowa zielona ikona trendu.
        extremeCard(
          'best',
          'ico--chart-up',
          t('reportPromptsEven'),
          t('reportPromptsEvenDesc', { count: sorted.length }),
          extremes.value
        )
      ) : (
        <>
          {extremeCard(
            'best',
            'ico--chart-up',
            t('reportPromptsBest'),
            promptLabel(extremes.best),
            extremes.best.visibility,
            extremes.best.groupSize
          )}
          {extremeCard(
            'worst',
            'ico--chart-down',
            t('reportPromptsWorst'),
            promptLabel(extremes.worst),
            extremes.worst.visibility,
            extremes.worst.groupSize
          )}
        </>
      )}
    </div>
  );

  const section = !extremes ? (
    <ReportSection
      title={t('reportPromptsTitle')}
      titleAccent={t('reportPromptsTitleAccent')}
      subtitle={t('reportPromptsSubtitle')}
    >
      {list}
    </ReportSection>
  ) : (
    <ReportSection
      title={t('reportPromptsTitle')}
      titleAccent={t('reportPromptsTitleAccent')}
      subtitle={t('reportPromptsSubtitle')}
      aside={<div className="report-section__panel">{list}</div>}
    >
      {extremesBlock}
    </ReportSection>
  );

  if (!locked) {
    return section;
  }

  // Rozmycie idzie na wnętrze karty (patrz SCSS): tytuł, podtytuł i treść znikają, ale sama sekcja
  // zostaje widoczna, żeby slajd nie zlał się z tłem. Podtytuł wjeżdża do środka kolumny, bo
  // `ReportSection` renderuje go w nagłówku, a ten i tak jest już pod rozmyciem.
  return (
    <div className="report-prompts__locked">
      <ReportSection
        title={t('reportPromptsTitle')}
        titleAccent={t('reportPromptsTitleAccent')}
        aside={
          <div className="report-section__panel" aria-hidden="true">
            {list}
          </div>
        }
      >
        <div aria-hidden="true">
          <blockquote className="report-section__subtitle">{t('reportPromptsSubtitle')}</blockquote>
          {extremesBlock}
        </div>
      </ReportSection>

      <div className="report-prompts__locked-cta">
        <span className="ico--lock ico--sm" aria-hidden="true" />
        <h3 className="report-prompts__locked-title">{t('reportPromptsLockedTitle')}</h3>
        <p className="report-prompts__locked-desc">{t('reportPromptsLockedDesc')}</p>
        {signUpHref && (
          <a className="btn btn--sm" href={signUpHref}>
            <span className="ico--zap ico--xs" aria-hidden="true" />
            {t('reportPromptsLockedCta')}
          </a>
        )}
      </div>
    </div>
  );
};

export default ReportPromptAnalysis;
